import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { s } from './Pattern.styles';

interface Props {
  onClose: () => void;
  selected?: string[];
  onApply: (value: string[]) => void;
}

const riskOptions = [
  { label: 'HUG인수조건변경' },
  { label: '임차인 대항력' },
  { label: '임금채권' },
  { label: '재매각' },
  { label: '위반건축물' },
  { label: '법정지상권' },
  { label: '분묘기지권' },
  { label: '유치권' },
];

const RiskFactorModal = ({ onClose, selected = [], onApply }: Props) => {
  const [selectedRisks, setSelectedRisks] = useState<string[]>(selected ?? []);

  const allValue = riskOptions.map((i) => i.label);

  const toggleAllOption = () => {
    if (isAll) {
      setSelectedRisks([]);
    } else {
      setSelectedRisks(riskOptions.map((i) => i.label));
    }
  };
  const toggleSelection = (label: string) => {
    if (selectedRisks.includes(label)) {
      setSelectedRisks(selectedRisks.filter((v) => v !== label));
    } else {
      setSelectedRisks([...selectedRisks, label]);
    }
  };

  const isAll = selectedRisks.length === riskOptions.length;

  const handleApply = () => {
    onApply(selectedRisks);
    onClose();
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return (
    <Overlay>
      <ModalContainer>
        <Header>
          <span>
            <ImgBox>
              <img
                src="/assets/images/arrow/arrow-left-black.svg"
                alt="back"
                onClick={onClose}
              />
            </ImgBox>
            상세리스크 요인
          </span>
          <ImgBox>
            <img
              src="/assets/images/close/close-dark-gray.svg"
              alt="close"
              onClick={onClose}
              style={{ cursor: 'pointer', width: 24 }}
            />
          </ImgBox>
        </Header>

        <Section style={{ marginTop: '2px' }}>
          <SectionTitle>리스크 요인</SectionTitle>
          <RiskList>
            {riskOptions.map((option) => (
              <s.CheckButtonLabel
                key={option.label}
                onChange={() => toggleSelection(option.label)}
              >
                <input
                  type="checkbox"
                  checked={selectedRisks.includes(option.label)}
                />
                <img
                  src={
                    selectedRisks.includes(option.label)
                      ? '/assets/images/icon/algo-check-icon-on.svg'
                      : '/assets/images/icon/algo-check-icon-off.svg'
                  }
                  alt={option.label}
                />
                {option.label}
              </s.CheckButtonLabel>
            ))}
          </RiskList>
        </Section>

        <BottomBar>
          {selectedRisks.length > 0 && (
            <SelectedTags>
              {selectedRisks.slice(0, 2).map((label) => (
                <Tag key={label}>
                  {label}
                  <RemoveButton
                    onClick={() =>
                      setSelectedRisks(selectedRisks.filter((r) => r !== label))
                    }
                  >
                    ×
                  </RemoveButton>
                </Tag>
              ))}
              {selectedRisks.length > 2 && (
                <Tag>+{selectedRisks.length - 2}</Tag>
              )}
            </SelectedTags>
          )}

          <BtnWrap>
            <ResetButton onClick={() => setSelectedRisks([])}>
              <img
                src={'/assets/images/icon/reload-gray.svg'}
                alt="초기화"
                style={{ width: 24 }}
              />
              초기화
            </ResetButton>
            <ApplyButton onClick={handleApply}>
              {selectedRisks.length > 0
                ? `${selectedRisks.length}개 적용`
                : '적용'}
            </ApplyButton>
          </BtnWrap>
        </BottomBar>
      </ModalContainer>
    </Overlay>
  );
};

export default RiskFactorModal;
const Overlay = styled.div`
  position: fixed;
  z-index: 2000;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const ModalContainer = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px;
  font-weight: bold;
  font-size: 16px;
  span {
    display: flex;
    align-items: center;
  }
`;
const ImgBox = styled.div`
  display: flex;
  padding: 10px;
  align-items: center;
`;

const Section = styled.div`
  padding: 16px;
`;

const SectionTitle = styled.div`
  color: #222;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 23.2px */
  letter-spacing: -0.32px;
`;

const SectionDescription = styled.div`
  margin-top: 4px;
  color: #616161;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const RadioGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  padding-top: 8px;
`;

const RadioItem = styled.label`
  display: flex;
  padding: 8px 0;
  align-items: center;
  gap: 4px;
  color: #616161;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.28px;
`;

const RiskList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 8px;
`;

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Tag = styled.div`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 2px;
  border-radius: 9999px;
  border: 1px solid #e1e2e4;
  background: rgba(112, 115, 124, 0.05);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  color: #222;
`;

const BottomBar = styled.div`
  position: fixed;
  bottom: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  width: 100%;
  background: #fff;
`;

const BtnWrap = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
`;

const ApplyButton = styled.div`
  display: flex;
  padding: 14px 28px;
  justify-content: center;
  align-items: center;
  gap: 6px;
  flex: 1 0 0;
  border-radius: 8px;
  background: #008aff;
  color: #fff;
  text-align: center;

  font-family: 'Spoqa Han Sans Neo';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 23.2px */
  letter-spacing: -0.32px;
`;
const ResetButton = styled.div`
  display: flex;
  flex-direction: column;
  height: 50px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  color: rgba(55, 56, 60, 0.61);
  text-align: center;
  font-family: Pretendard;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px; /* 120% */
`;
const RemoveButton = styled.div`
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
`;
